// Serial governance orchestrator (§ serial), incremental. Input is the name-based
// body (assembled NameScript) — NOT the roster. It walks episodes IN ORDER with a
// context that starts EMPTY: per episode the model reads that episode's body plus
// the context-so-far and reports discovered entities / 造型 / and per
// scene who is present in which look. Nothing is seeded from the front's extracted
// assets/states.
//
// The final v3 is built FROM the accumulated registry: the serial scene refs are
// injected into the body, assignProvisionalIds mints ids + assets, the registry's
// descriptions/looks are overlaid, and governDeterministic materializes.
// Per-episode md checkpoint + transient-retry as before.

import { CliError, EXIT_INPUT } from "../../common.js";
import {
  assignProvisionalIds,
  type NameScript,
  type NameScene,
  type StructuralIdentityKeys,
} from "../../domain/ingest/assembler.js";
import { governDeterministic } from "../../domain/ingest/govern.js";
import { emptyContext, applyDelta, compact, pendingArcFold, applyArcFold, synopsisLabel, render, resolveCanon, findEntity, baseLook, estimateTokens, packEpisodes, isCrowdName, type GlobalContext, type Delta } from "../../domain/ingest/serial-context.js";
import { runArcSummary } from "../../llm/tasks/ingest/arc-summary.js";
import { runEpisodeAssets, toDelta, SerialDeltaShapeError } from "../../llm/tasks/ingest/episode-assets.js";
import { runNameFix, nameFixToDelta } from "../../llm/tasks/ingest/name-fix.js";
import type { Importance } from "../../domain/script/shared/types.js";
import type { LlmTaskExecutor } from "../../llm/types.js";

export interface SerialCheckpoint {
  // Keyed by the window's [startEp, endEp] range (a window covers 1+ contiguous
  // episodes parsed together); resume re-packs identically and hits the cache.
  readUpdate(startEp: number, endEp: number): string | undefined;
  writeStep(startEp: number, endEp: number, contextMd: string, updateMd: string): void;
  // Arc summaries are keyed by the folded episode range so resume replays them
  // from cache (no repeated LLM); the range is deterministic (pure compaction).
  readArc?(startEp: number, endEp: number): string | undefined;
  writeArc?(startEp: number, endEp: number, summaryMd: string): void;
  // Per-window name-fix repair (raw tool JSON), keyed by the window range so
  // resume replays it from cache like the window update itself.
  readNameFix?(startEp: number, endEp: number): string | undefined;
  writeNameFix?(startEp: number, endEp: number, raw: string): void;
}
export interface SerialWindowTiming { startEp: number; endEp: number; ms: number; cached: boolean; index: number; total: number; totalEpisodes: number }
export interface SerialGovernOptions {
  executor: LlmTaskExecutor;
  signal?: AbortSignal;
  checkpoint?: SerialCheckpoint;
  force?: boolean;
  windowTokens?: number;
  // Original front matter (角色卡/设定) that pass0 stripped, capped — fed to the
  // model as 原文设定 reference so descriptions can lean on the source's own wording.
  sourceRef?: string;
  // Fired after each window (parsed or cached), with its wall-clock ms — drives
  // the live timing/ETA in `view --watch` and `ingest status`.
  onWindow?: (t: SerialWindowTiming) => void;
}
export interface SerialGovernResult {
  doc: Record<string, unknown>;
  identityKeys: StructuralIdentityKeys;
  episodes: number;
  reused: number;
}

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const isArr = (v: unknown): v is unknown[] => Array.isArray(v);

// featured iff an asset appears at ≥2 points in the global action stream AND those
// points span ≥ this many actions apart — a real recurring presence, not a single
// scene's chatter. Tunable after observing results.
const FEATURED_MIN_SPAN = 5;
const norm = (s: string): string => s.normalize("NFC").replace(/\s+/g, " ").trim();
// A checkpointed window is the tool's raw JSON. Top-level parse failure → null so
// the caller can re-run the window instead of projecting an empty Delta (the old
// `{}` fallback silently wiped entity/look registration for that window).
const parseRaw = (raw: string): unknown | null => {
  try { return JSON.parse(raw); } catch { return null; }
};

// Include serial_delta_shape so double-encoded / unparseable array fields re-call
// the model instead of shipping an empty registry for the window.
// A successful HTTP response can still omit the forced tool call, return an empty
// body, or stop before the structured payload is complete. Those are transient
// generation failures in an ingest window, just like a timeout: retry the same
// semantic unit before asking the outer caller to resume the whole workspace.
const RETRYABLE = new Set([
  "timeout",
  "rate_limited",
  "request_failed",
  "endpoint",
  "empty_response",
  "invalid_response",
  "truncated",
  "serial_delta_shape",
]);
const isRetryable = (e: unknown): boolean =>
  !!e && typeof e === "object" && "code" in e && RETRYABLE.has(String((e as { code: unknown }).code));
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const winLabel = (startEp: number, endEp: number): string => (startEp === endEp ? `episode ${startEp}` : `episodes ${startEp}-${endEp}`);

async function windowUpdate(executor: LlmTaskExecutor, startEp: number, endEp: number, contextMd: string, bodyMd: string, signal?: AbortSignal): Promise<{ delta: Delta; raw: string }> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await runEpisodeAssets(executor, contextMd, bodyMd, signal);
    } catch (e) {
      if (isRetryable(e) && attempt < 3 && !signal?.aborted) { await sleep(3000 * attempt); continue; }
      throw new CliError(`serial govern failed at ${winLabel(startEp, endEp)}.`, {
              exitCode: EXIT_INPUT,
              errorCode: "INGEST_SERIAL_FAILED",
              hint: e instanceof SerialDeltaShapeError
                ? `Model returned a malformed serial delta (${e.message}). Rerun \`scriptctl ingest\` (optionally --force) to retry ${winLabel(startEp, endEp)}.`
                : `Rerun \`scriptctl ingest\` to resume from ${winLabel(startEp, endEp)} (earlier windows are cached).`,
              data: { actual: [e instanceof Error ? e.message : String(e)] },
            });
    }
  }
}

function assertNameFixTargets(ctx: GlobalContext, delta: Delta): void {
  const missing = delta.merges
    .map((merge) => merge.into)
    .filter((target) => !findEntity(ctx, target));
  if (missing.length) {
    throw new SerialDeltaShapeError(`name-fix merge target is not registered: ${[...new Set(missing)].join("、")}`);
  }
}

// Name-fix is required for every unresolved actor/location. A structurally valid
// but incomplete repair is retried in the same window instead of reaching govern
// as an asset with empty semantics.
async function nameFixRetry(
  executor: LlmTaskExecutor,
  startEp: number,
  endEp: number,
  contextMd: string,
  names: string[],
  ctx: GlobalContext,
  signal?: AbortSignal,
): Promise<{ delta: Delta; raw: string }> {
  for (let attempt = 1; ; attempt++) {
    try {
      const produced = await runNameFix(executor, contextMd, names, signal);
      assertNameFixTargets(ctx, produced.delta);
      return produced;
    } catch (e) {
      if (isRetryable(e) && attempt < 3 && !signal?.aborted) { await sleep(3000 * attempt); continue; }
      throw new CliError(`serial name repair failed at ${winLabel(startEp, endEp)}.`, {
        exitCode: EXIT_INPUT,
        errorCode: "INGEST_SERIAL_NAME_FIX_FAILED",
        hint: e instanceof SerialDeltaShapeError
          ? `Model returned an incomplete name repair (${e.message}). Rerun \`scriptctl ingest\` to retry ${winLabel(startEp, endEp)}.`
          : `Rerun \`scriptctl ingest\` to resume from ${winLabel(startEp, endEp)} (earlier windows are cached).`,
        data: { actual: [e instanceof Error ? e.message : String(e)] },
      });
    }
  }
}

export async function runSerialGovern(body: NameScript, opts: SerialGovernOptions): Promise<SerialGovernResult> {
  const ctx = emptyContext();
  const episodes = body.episodes ?? [];
  let reused = 0;

  // Pack contiguous episodes into token-bounded windows and parse each window in
  // one call. Bodies + token sizes are precomputed so packing is deterministic.
  const bodies = episodes.map(renderEpisodeBody);
  const windows = packEpisodes(bodies.map(estimateTokens), opts.windowTokens);

  for (let index = 0; index < windows.length; index++) {
    const [startEp, endEp] = windows[index]!;
    const contextMd = render(ctx, endEp);
    // Each body already opens with `# ep_NNN`, so the model sees the episode splits.
    const bodyMd = bodies.slice(startEp - 1, endEp).join("\n\n");

    // Prepend the source 原文设定 reference (static) to what the model sees; the
    // checkpoint trace stores only the registry contextMd (the ref is redundant 23×).
    const promptCtx = opts.sourceRef ? `# 原文设定（参考，来自原稿开头）\n${opts.sourceRef}\n\n${contextMd}` : contextMd;

    const t0 = Date.now();
    // The checkpoint stores the window's raw tool JSON; resume re-projects it to a
    // Delta (deterministic), so a cached window costs no model call. Corrupt or
    // shape-invalid cache (double-encoded garbage, unescaped quotes) is re-run —
    // never applied as an empty Delta.
    const cached = opts.force ? undefined : opts.checkpoint?.readUpdate(startEp, endEp);
    let delta: Delta;
    let cachedHit = false;
    if (cached !== undefined) {
      const parsed = parseRaw(cached);
      try {
        if (parsed == null) throw new SerialDeltaShapeError("checkpoint top-level JSON is unparseable");
        delta = toDelta(parsed);
        cachedHit = true;
        reused++;
        opts.executor.recordReuse("ingest.episode-assets");
      } catch (e) {
        if (!(e instanceof SerialDeltaShapeError)) throw e;
        const produced = await windowUpdate(opts.executor, startEp, endEp, promptCtx, bodyMd, opts.signal);
        delta = produced.delta;
        opts.checkpoint?.writeStep(startEp, endEp, contextMd, produced.raw);
      }
    } else {
      const produced = await windowUpdate(opts.executor, startEp, endEp, promptCtx, bodyMd, opts.signal);
      delta = produced.delta;
      opts.checkpoint?.writeStep(startEp, endEp, contextMd, produced.raw);
    }
    applyDelta(ctx, delta, startEp, endEp);

    // Name-fix repair, IN THIS WINDOW (whichever window meets an unregistered name
    // fixes it here, not in a giant final pass). After applying this window's delta,
    // any actor/location the body names but the registry still can't resolve is a
    // missed merge — ask the model (merge-into-existing or new) and apply, so the
    // registry is complete before we move on. Best-effort: a gateway failure skips
    // the fix for this window (names ship as-is) rather than aborting the run.
    const unresolved = collectUnresolved(episodes.slice(startEp - 1, endEp), ctx);
    if (unresolved.length) {
      const cachedFix = opts.force ? undefined : opts.checkpoint?.readNameFix?.(startEp, endEp);
      if (cachedFix !== undefined) {
        const parsedFix = parseRaw(cachedFix);
        try {
          if (parsedFix == null) throw new SerialDeltaShapeError("name-fix checkpoint top-level JSON is unparseable");
          const fixDelta = nameFixToDelta(parsedFix, unresolved);
          assertNameFixTargets(ctx, fixDelta);
          applyDelta(ctx, fixDelta, startEp, endEp);
          reused++;
          opts.executor.recordReuse("ingest.name-fix");
        } catch (e) {
          if (!(e instanceof SerialDeltaShapeError)) throw e;
          const produced = await nameFixRetry(opts.executor, startEp, endEp, render(ctx, endEp), unresolved, ctx, opts.signal);
          opts.checkpoint?.writeNameFix?.(startEp, endEp, produced.raw);
          applyDelta(ctx, produced.delta, startEp, endEp);
        }
      } else {
        const produced = await nameFixRetry(opts.executor, startEp, endEp, render(ctx, endEp), unresolved, ctx, opts.signal);
        opts.checkpoint?.writeNameFix?.(startEp, endEp, produced.raw);
        applyDelta(ctx, produced.delta, startEp, endEp);
      }
    }

    compact(ctx);
    // Fold the far synopsis into arc summaries when over budget. Deterministic
    // range → cached by [startEp, endEp] so resume never re-summarizes.
    for (let group = pendingArcFold(ctx); group; group = pendingArcFold(ctx)) {
      const startEp = group[0]!.startEp;
      const endEp = group[group.length - 1]!.endEp;
      const cachedArc = opts.force ? undefined : opts.checkpoint?.readArc?.(startEp, endEp);
      let summary: string;
      if (cachedArc !== undefined) {
        summary = cachedArc;
        reused++;
        opts.executor.recordReuse("ingest.arc-summary");
      }
      else {
        const foldedText = group.map((g) => `${synopsisLabel(g)}：${g.line}`).join("\n");
        summary = await runArcSummary(opts.executor, `第${startEp}集–第${endEp}集`, foldedText, opts.signal);
        opts.checkpoint?.writeArc?.(startEp, endEp, summary);
      }
      applyArcFold(ctx, summary);
    }
    opts.onWindow?.({ startEp, endEp, ms: Date.now() - t0, cached: cachedHit, index, total: windows.length, totalEpisodes: episodes.length });
  }

  const finalized = finalize(body, ctx);
  return {
    ...finalized,
    episodes: episodes.length,
    reused,
  };
}

// Body ACTOR/LOCATION names (in the given episodes) that resolve to no registry
// entity — a missed merge worth repairing (a real character/place the serial pass
// forgot to register). PROPS are excluded on purpose: the body lists every
// incidental object, but the registry deliberately keeps only 关键道具, so an
// unregistered prop is a background object we DROP (finalize skips it), not a fix.
function collectUnresolved(eps: NameScript["episodes"], ctx: GlobalContext): string[] {
  const set = new Set<string>();
  const check = (name: string | undefined): void => {
    if (!name) return;
    const canon = resolveCanon(ctx, name);
    if (isCrowdName(canon)) return;
    if (!findEntity(ctx, canon)) set.add(canon);
  };
  for (const ep of eps) {
    for (const sc of ep.scenes) {
      check(sc.location?.name);
      for (const a of sc.actors) check(a.name);
      for (const ac of sc.actions) if (ac.speaker && "name" in ac.speaker) check(ac.speaker.name);
    }
  }
  return [...set];
}

const SPACE: Record<string, string> = { interior: "内", exterior: "外" };
const TIME: Record<string, string> = { day: "日", night: "夜" };

// Render one episode's body as readable 正文 — scene id + time/space + the source's
// declared 地点/在场/道具 (so the model can lean on the original text's own names +
// descriptions) + the action/dialogue lines (speaker by name). Inline descriptions
// live in the action text; the model still emits its own scene presence refs.
export function renderEpisodeBody(ep: { episode_id: string; title?: string; scenes: NameScene[] }): string {
  const out: string[] = [`# ${ep.episode_id}${ep.title ? ` ${ep.title}` : ""}`];
  for (const sc of ep.scenes) {
    out.push(`## ${sc.scene_id}`);
    const env = [SPACE[sc.environment?.space ?? ""], TIME[sc.environment?.time ?? ""]].filter(Boolean).join(" ");
    if (env) out.push(`时空：${env}`);
    if (sc.location?.name) out.push(`地点：${sc.location.name}`);
    if (sc.actors.length) out.push(`在场：${sc.actors.map((a) => a.name).join("、")}`);
    if (sc.props.length) out.push(`道具：${sc.props.map((p) => p.name).join("、")}`);
    for (const ac of sc.actions) {
      const sp = ac.speaker ? ("name" in ac.speaker ? ac.speaker.name : `[${ac.speaker.kind}]`) : "";
      const emo = ac.emotion ? `（${ac.emotion}）` : "";
      if (ac.type === "dialogue") out.push(`${sp}${emo}：${ac.content}`);
      else if (ac.type === "inner_thought") out.push(`${sp}（心）${emo}：${ac.content}`);
      else if (ac.type === "transition") out.push(`【转场】${ac.content}`);
      else out.push(ac.content);
    }
  }
  return out.join("\n");
}

// Build the final v3 doc from the body + accumulated registry. Inject serial scene
// refs (canonical names + look as state), mint ids via assignProvisionalIds, overlay
// registry description/aliases/look-descriptions, then materialize.
function finalize(
  body: NameScript,
  ctx: GlobalContext,
): { doc: Record<string, unknown>; identityKeys: StructuralIdentityKeys } {
  const doc: NameScript = JSON.parse(JSON.stringify(body));
  // The BODY (from normalize) is the source of who's on screen and every dialogue
  // line — the model's job was only to build the registry + correct the names. So
  // here we resolve every body-written name to its registry canonical (this IS the
  // "correct the transcript" step), rewrite dialogue text likewise, and derive each
  // scene's 造型 from the sparse lookChanges carried forward. The model's scene
  // presence lists are NOT used.
  const carry = new Map<string, string>(); // canonical → current 造型 (continuity)
  for (const ep of doc.episodes) {
    for (const sc of ep.scenes) {
      // register is the source of truth: keep only refs that resolve to a
      // registered entity. Unregistered names are dropped (an unregistered prop is a
      // background object; any actor/location worth keeping was fixed by name-fix).
      const reg = (n: string): boolean => !!findEntity(ctx, n);
      const loc = sc.location ? resolveCanon(ctx, sc.location.name) : null;
      let actors = dedupeNames(sc.actors.map((a) => resolveCanon(ctx, a.name)).filter((n) => !isCrowdName(n) && reg(n)));
      const props = dedupeNames(sc.props.map((p) => resolveCanon(ctx, p.name)).filter(reg));
      const speaking: string[] = [];
      for (const ac of sc.actions) {
        if (ac.speaker && "name" in ac.speaker) {
          const canon = resolveCanon(ctx, ac.speaker.name);
          if (isCrowdName(canon)) ac.speaker = { kind: "group", label: canon };
          else { ac.speaker.name = canon; if (reg(canon)) speaking.push(canon); }
        }
        // ponytail: 只归一 speaker，正文 content 逐字保留。别名字符串替换会
        // 双替（圣典→英雄圣典 命中已有 英雄圣典）并误改画面/字幕原文（RELIC、
        // CHRONICLE OF HEROES 等 UI 字面）。要恢复正文改名须做实体感知替换，非朴素 split/join。
      }
      for (const canon of speaking) if (!actors.some((n) => norm(n) === norm(canon))) actors.push(canon); // speaker ⇒ present
      // 造型: apply this scene's changes to the carry, then stamp each ref (inherit).
      for (const lc of ctx.lookChanges[sc.scene_id] ?? []) carry.set(resolveCanon(ctx, lc.entity), lc.look);
      sc.location = loc && reg(loc) ? stateRef(ctx, loc, carry) : null;
      sc.actors = actors.map((n) => stateRef(ctx, n, carry));
      sc.props = props.map((n) => stateRef(ctx, n, carry));
    }
  }

  const assigned = assignProvisionalIds(doc);
  const script = assigned.script;
  overlay(script, ctx);
  applyImportance(script); // deterministic: override model/registry tiers by on-screen span
  const governed = governDeterministic(script);
  renameSyntheticDefault(governed);
  assertDescriptions(governed);
  return { doc: governed, identityKeys: assigned.identityKeys };
}

const RESET = "常态";

// De-dupe a list of already-resolved canonical names, keeping first-seen order.
function dedupeNames(names: string[]): string[] {
  const seen = new Set<string>();
  return names.filter((n) => { const k = norm(n); if (!n || seen.has(k)) return false; seen.add(k); return true; });
}

// A canonical name + its 造型 for a scene: the carried look, else the entity's
// baseline 常态. Every on-screen ref binds a state (never null/未标注).
function stateRef(ctx: GlobalContext, canon: string, carry: Map<string, string>): { name: string; state?: string } {
  const e = findEntity(ctx, canon);
  const look = carry.get(canon) ?? (e ? baseLook(e) : undefined) ?? RESET;
  return { name: canon, state: look };
}

// Assets with no discovered look keep a synthetic default whose name leaked as the
// literal "default"; relabel it to 常态 for a meaningful baseline.
function renameSyntheticDefault(script: Obj): void {
  for (const listKey of ["actors", "locations", "props"] as const) {
    for (const asset of isArr(script[listKey]) ? (script[listKey] as unknown[]) : []) {
      if (!isObj(asset)) continue;
      for (const st of isArr(asset.states) ? asset.states : []) {
        if (isObj(st) && st.state_id === "default" && st.state_name === "default") st.state_name = "常态";
      }
    }
  }
}

// Stamp registry descriptions, aliases, and per-look state descriptions onto the
// minted assets. Importance is derived later from the complete action stream.
function overlay(script: Obj, ctx: GlobalContext): void {
  for (const [listKey, nameKey] of [["actors", "actor_name"], ["locations", "location_name"], ["props", "prop_name"]] as const) {
    for (const asset of isArr(script[listKey]) ? (script[listKey] as unknown[]) : []) {
      if (!isObj(asset)) continue;
      const e = findEntity(ctx, String(asset[nameKey]));
      const states = isArr(asset.states) ? asset.states : [];
      if (e) {
        if (e.description) asset.description = e.description;
        if (e.aliases.length) asset.aliases = [...new Set([...(isArr(asset.aliases) ? (asset.aliases as string[]) : []), ...e.aliases])];
        const baseName = baseLook(e); // baseline (常态) look NAME for the synthetic default state
        const baseDesc = baseName ? (e.looks.find((l) => norm(l.name) === norm(baseName))?.description ?? "") : "";
        for (const st of states) {
          if (!isObj(st)) continue;
          if (st.state_id === "default") { if (baseDesc) st.description = baseDesc; continue; }
          const look = e.looks.find((l) => norm(l.name) === norm(String(st.state_name)));
          if (look?.description) st.description = look.description;
        }
      }
    }
  }
}

// Deterministic importance (§ replaces the model's judgment call). Walk the whole
// doc as one ordered action stream; every asset records the action index of each
// point it's "on screen" — a dialogue/inner-thought speaker at that action, or a
// member of a scene's cast/props/location (counted at the scene's first action).
// featured iff it surfaces at ≥2 points spanning ≥ FEATURED_MIN_SPAN actions apart;
// everything else background. Fully overrides whatever overlay stamped.
function applyImportance(script: Obj): void {
  const first = new Map<string, number>(); // assetId → first on-screen action index
  const last = new Map<string, number>();  // assetId → last on-screen action index
  const count = new Map<string, number>(); // assetId → number of on-screen points
  let idx = 0;
  const mark = (id: unknown): void => {
    if (typeof id !== "string") return;
    if (!first.has(id)) first.set(id, idx);
    last.set(id, idx);
    count.set(id, (count.get(id) ?? 0) + 1);
  };
  for (const ep of isArr(script.episodes) ? script.episodes : []) {
    if (!isObj(ep) || !isArr(ep.scenes)) continue;
    for (const sc of ep.scenes) {
      if (!isObj(sc)) continue;
      // Scene membership (cast/props/location) counts at the scene's first action.
      const members: unknown[] = [
        ...(isArr(sc.actors) ? sc.actors : []),
        ...(isArr(sc.props) ? sc.props : []),
        ...(isObj(sc.location) ? [sc.location] : []),
      ];
      const memberIds = members.filter(isObj).map((m) => m.actor_id ?? m.prop_id ?? m.location_id);
      for (const id of memberIds) mark(id);
      for (const ac of isArr(sc.actions) ? sc.actions : []) {
        if (isObj(ac) && isObj(ac.speaker) && typeof ac.speaker.actor_id === "string") mark(ac.speaker.actor_id);
        idx++; // every action advances the global stream, speaker or not
      }
    }
  }
  const featured = (id: string): boolean =>
    (count.get(id) ?? 0) >= 2 && (last.get(id)! - first.get(id)!) >= FEATURED_MIN_SPAN;
  for (const [listKey, idKey] of [["actors", "actor_id"], ["props", "prop_id"]] as const) {
    for (const asset of isArr(script[listKey]) ? (script[listKey] as unknown[]) : []) {
      if (!isObj(asset)) continue;
      asset.importance = (featured(String(asset[idKey])) ? "featured" : "background") as Importance;
    }
  }
  // Locations are always featured — every place that reaches the script is worth a
  // background plate; there is no "龙套 location" to skip.
  for (const asset of isArr(script.locations) ? (script.locations as unknown[]) : []) {
    if (isObj(asset)) asset.importance = "featured" as Importance;
  }
}

// Final invariant after governDeterministic has rebuilt state ids: identity and
// look descriptions must already be complete. Missing content is an ingest error;
// identity text is never copied into a look to manufacture completeness.
function assertDescriptions(script: Obj): void {
  const nonEmpty = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
  for (const [listKey, nameKey] of [["actors", "actor_name"], ["locations", "location_name"], ["props", "prop_name"]] as const) {
    for (const asset of isArr(script[listKey]) ? (script[listKey] as unknown[]) : []) {
      if (!isObj(asset)) continue;
      const assetName = String(asset[nameKey] ?? "");
      const states = isArr(asset.states) ? asset.states : [];
      if (!nonEmpty(asset.description)) {
        throw new CliError(`Ingested asset "${assetName}" is missing its identity description.`, {
          exitCode: EXIT_INPUT,
          errorCode: "ASSET_DESCRIPTION_MISSING",
          hint: "Rerun `scriptctl ingest --force` so the serial asset window can regenerate this entity.",
        });
      }
      for (const st of states) {
        if (!isObj(st) || nonEmpty(st.description)) continue;
        const stateName = String(st.state_name ?? "");
        throw new CliError(`Ingested asset "${assetName}" has no visual description for look "${stateName}".`, {
          exitCode: EXIT_INPUT,
          errorCode: st.state_id === "default" ? "DEFAULT_LOOK_DESCRIPTION_MISSING" : "LOOK_DESCRIPTION_MISSING",
          hint: "Rerun `scriptctl ingest --force` so the serial asset window can regenerate this look.",
        });
      }
    }
  }
}
